import * as p from '@clack/prompts'
import type { BetterstartCommandRuntime, Cancellable } from '@cli-types/core-engine/commands.js'
import {
  ADD_COMMAND,
  ALL_FLAG,
  CANCELLED,
  CREATE_COMMAND,
  CREATE_SCHEMA_KINDS,
  GENERATE_COMMAND,
  REMOVE_COMMAND,
  REMOVE_SCHEMA_COMMAND,
  UPDATE_COMMAND
} from '@cli-constants/commands.js'
import { promptComponents } from './prompt-components.js'
import { promptInstallables } from './prompt-installables.js'

/**
 * Commands the menu cannot dispatch bare. Four declare required arguments and
 * would hit commander's `missing required argument`; `generate` and `update`
 * look argument-free to commander but reject an empty invocation themselves,
 * because their operand is only optional when the matching flag is supplied.
 */
export async function promptRequiredArguments(
  name: string,
  runtime: BetterstartCommandRuntime,
  cwd: string
): Promise<Cancellable<string[]>> {
  if (name === CREATE_COMMAND) {
    const kind = await p.select({
      message: 'Schema kind',
      options: CREATE_SCHEMA_KINDS.map((value) => ({ value, label: value }))
    })
    return p.isCancel(kind) ? CANCELLED : [kind]
  }

  if (name === ADD_COMMAND || name === REMOVE_COMMAND) {
    return promptInstallables(name, runtime, cwd)
  }

  if (name === GENERATE_COMMAND) {
    const schemas = await runtime.listSchemaChoices(cwd)
    if (schemas.length === 0) {
      p.log.warn('No schemas to generate.')
      return CANCELLED
    }

    const target = await p.select({
      message: 'What do you want to generate?',
      options: [
        ...schemas.map((value) => ({ value, label: value })),
        { value: ALL_FLAG, label: 'All schemas', hint: 'regenerate everything' }
      ]
    })
    return p.isCancel(target) ? CANCELLED : [target]
  }

  if (name === UPDATE_COMMAND) {
    return promptComponents(runtime, cwd)
  }

  if (name === REMOVE_SCHEMA_COMMAND) {
    const schemas = await runtime.listSchemaChoices(cwd)
    if (schemas.length === 0) {
      p.log.warn('No schemas to remove.')
      return CANCELLED
    }

    const schema = await p.select({
      message: 'Schema to remove',
      options: schemas.map((value) => ({ value, label: value }))
    })
    return p.isCancel(schema) ? CANCELLED : [schema]
  }

  return []
}
